/**
 * UserTypeRadioGroup — autenticazione Phase 5 (IMPL-P5-008).
 *
 * Three selectable cards mapping to the `TipologiaUtente` enum
 * (`PRIVATO` / `AGENTE` / `COMMERCIALISTA`). Each card pairs a Lucide icon
 * with a label + brief subtitle. The selected card is visually emphasized
 * (`border-primary ring-2 ring-primary/20`) and its inner radio dot
 * indicates the active value.
 *
 * Accessibility:
 *  - Uses Radix `RadioGroup` semantics under the hood — the `<label>`
 *    wrappers turn each card into the implicit click target.
 *  - `aria-invalid` flips when the parent form passes `invalid`.
 */
import { forwardRef } from 'react';
import { Building2, Calculator, User } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

export type Tipologia = 'PRIVATO' | 'AGENTE' | 'COMMERCIALISTA';

interface UserTypeRadioGroupProps {
  value?: Tipologia;
  onValueChange?: (value: Tipologia) => void;
  /** Common name passed by react-hook-form so each radio shares the same group. */
  name?: string;
  invalid?: boolean;
  describedBy?: string;
}

const OPTIONS: Array<{
  value: Tipologia;
  title: string;
  subtitle: string;
  icon: typeof User;
}> = [
  { value: 'PRIVATO', title: 'Privato', subtitle: 'Persona fisica', icon: User },
  { value: 'AGENTE', title: 'Agente', subtitle: 'Agenzia immobiliare', icon: Building2 },
  { value: 'COMMERCIALISTA', title: 'Commercialista', subtitle: 'Studio professionale', icon: Calculator },
];

export const UserTypeRadioGroup = forwardRef<HTMLDivElement, UserTypeRadioGroupProps>(
  ({ value, onValueChange, name, invalid, describedBy }, ref) => {
    return (
      <RadioGroup
        ref={ref}
        value={value}
        onValueChange={(v) => onValueChange?.(v as Tipologia)}
        name={name}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className="grid grid-cols-1 gap-2 sm:grid-cols-3"
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = value === opt.value;
          const itemId = `tipologia-${opt.value}`;
          return (
            <label
              key={opt.value}
              htmlFor={itemId}
              className={cn(
                'flex cursor-pointer flex-col items-start gap-2 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-accent/30 focus-within:ring-1 focus-within:ring-ring',
                selected && 'border-primary ring-2 ring-primary/20',
              )}
            >
              <div className="flex w-full items-start justify-between gap-2">
                <Icon
                  className={cn('h-5 w-5', selected ? 'text-primary' : 'text-muted-foreground')}
                  aria-hidden="true"
                />
                <RadioGroupItem id={itemId} value={opt.value} />
              </div>
              <div className="space-y-0.5">
                <p className="text-sm font-medium leading-none">{opt.title}</p>
                <p className="text-xs text-muted-foreground">{opt.subtitle}</p>
              </div>
            </label>
          );
        })}
      </RadioGroup>
    );
  },
);

UserTypeRadioGroup.displayName = 'UserTypeRadioGroup';
